import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { cloneDeep, mapValues } from 'lodash';
import { addDays, getDay, isValid } from 'date-fns';
import { generateId } from '../utils/generate-id';
// Types
import { Column } from '../objects/column';
import { CircuitState } from '../objects/circuitState';
import { CircuitLocalized } from '../objects/circuitLocalized';
import { Preferences } from '../../profile/objects/preferences';
import { ListNetworkData } from '../../utils/list/objects/listNetworkData';
// Action Types
import {
    ADD_CIRCUIT_PERIOD,
    ADD_PREFERENCES,
    CircuitActionTypes,
    DELETE_CIRCUIT_PERIOD,
    DELETE_CIRCUIT_PICTURE, DELETE_CIRCUIT_PRICE,
    MOVE_DOWN_COLUMN,
    MOVE_UP_COLUMN,
    RESET_ADD_CIRCUIT,
    RESET_FILTERS,
    SET_ACCOMODATION,
    SET_CAR,
    SET_CIRCUIT,
    SET_CIRCUIT_PRICES,
    SET_CUSTOM_PRODUCTS,
    SET_EDIT,
    SET_EDIT_STEP,
    SET_ERRORS,
    SET_EXPORT_TYPE,
    SET_FLIGHT,
    SET_LIMIT,
    SET_LIST,
    SET_MANUAL_PRODUCTS,
    SET_MASTER_PRICE,
    SET_ORDER_BY,
    SET_PAGE,
    SET_PREFERENCES as SET_CIRCUIT_PREFERENCES,
    SET_PRICES_FLIGHT,
    SET_PRICES_TERRESTRIAL,
    SET_PRODUCT_PERIOD,
    SET_RUNNING,
    SET_SEARCH,
    SET_TRANSFERS,
    TOGGLE_CANCEL_MODAL,
    TOGGLE_CIRCUIT_PICTURES_ORDER,
    TOGGLE_COLUMN,
    TOGGLE_CONTRACT_CREATE,
    TOGGLE_CREATE,
    TOGGLE_EXPORT,
    TOGGLE_EXPORT_FULL,
    TOGGLE_FILTERS,
    TOGGLE_REFRESH,
    UPDATE_CATEGORIES,
    UPDATE_CIRCUIT_NAME_LOCALE,
    UPDATE_CIRCUIT_NAME_LOCALES,
    UPDATE_CIRCUIT_PICTURE_ORDER,
    UPDATE_CONTRACT,
    UPDATE_CONTRACT_SELECTED,
    UPDATE_DESTINATION,
    UPDATE_DURATION_DAYS,
    UPDATE_DURATION_NIGHTS,
    UPDATE_FILTER_CATEGORIES,
    UPDATE_FILTER_COUNTRY,
    UPDATE_FILTER_CREATED_DATE_GTE,
    UPDATE_FILTER_CREATED_DATE_LTE,
    UPDATE_FILTER_CREATOR,
    UPDATE_FILTER_DESTINATION,
    UPDATE_FILTER_SOURCE,
    UPDATE_FILTER_PROVIDERS,
    UPDATE_FILTER_LANGUAGES,
    UPDATE_FILTER_LAST_AUTHOR,
    UPDATE_FILTER_REFERENCE,
    UPDATE_FILTER_MODIFIED_DATE_GTE,
    UPDATE_FILTER_MODIFIED_DATE_LTE,
    UPDATE_FILTER_PERIOD,
    UPDATE_FILTER_PERIOD_TYPE,
    UPDATE_FILTER_STATUS,
    UPDATE_FILTER_TYPE,
    UPDATE_LIST_ITEM_VISIBILITY,
    UPDATE_PERIOD_DAYS,
    UPDATE_PERIOD_END_DATE,
    UPDATE_PERIOD_START_DATE,
    UPDATE_PRICE_TYPE,
    UPDATE_PRODUCTS,
    UPDATE_PRODUCTS_STACK,
    UPDATE_PROVIDER,
    UPDATE_REFERENCE,
    UPDATE_EXTERNAL_REFERENCE,
    UPDATE_DISPLAY_FOR_PACKAGE,
    UPDATE_DISPLAY_FOR_TRIP_PLAN,
    UPDATE_RETURN_FLIGHT,
    UPDATE_SALE_PITCH_LOCALE,
    UPDATE_TRIP,
    UPDATE_TYPE,
    SET_DUPLICATE_CIRCUIT,
    UPDATE_SELECTED_CIRCUIT,
    UPDATE_MANY_CIRCUIT_VISIBILITY,
    SET_COLUMNS,
    SET_IS_PROTECTED_DESCRIPTION
} from './actionTypes';
import { ADD_CUSTOM_PICTURES, PictureActionTypes, SET_PICTURE } from '../../picture/redux/actionTypes';
import { ProfileActionTypes, SET_PREFERENCES } from '../../profile/redux/actionTypes';
import { PricePeriod } from '../objects/price-period';
import { TerrestrialPrice } from '../objects/terrestrialPrice';
import { ModelPrice } from '../../priceModel/objects/modelPrice';
import { FlightPrice } from '../objects/flightPrice';
import { Airport } from '../objects/airport';
import { Promotion } from '../objects/promotion';
import { Itinerary } from '../objects/itinerary';
import { ItineraryByDay } from '../objects/itineraryByDay';
import { Product } from '../objects/product';
import { Picture } from '../../picture/objects/picture';
import { Contract } from '../../customProvider/objects/contract';
import {
    PricesParametersDatesMode,
    PricesParametersDatesSelection
} from '../circuitPricesParameters';
import { Currency } from '../../currency/objects/currency';
import { FlightParams } from '../objects/flightParams';
import { FlightParamsListConfiguration } from '../objects/flightParamsListConfiguration';
import { FlightParamsListCriteria } from '../objects/flightParamsListCriteria';
import { setFlightParamsPreference } from '../utils/setFlightParamsPreference';
import { EMPTY_CIRCUIT_INFO_ERRORS, EMPTY_FLIGHT_PARAMS_LIST_CONFIGURATION, EMPTY_FLIGHT_PARAMS_LIST_CRITERIA } from '../constants/flightParamsConstants';
import { CircuitInfoErrors } from '../objects/circuitInfoErrors';


const initialState: CircuitState = {
    selected_circuit: [],
    cancel_modal: false,
    columns: [{
        label: 'type',
        field_name: 'TYPE',
        checked: true,
        order_by: 'type',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'name',
        field_name: 'NAME',
        checked: true,
        order_by: 'name',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'languages',
        field_name: 'LANGUAGES',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'providers',
        field_name: 'PROVIDERS',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'source',
        field_name: 'SOURCE',
        checked: true,
        order_by: 'source',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'reference',
        field_name: 'REFERENCE',
        checked: true,
        order_by: 'reference',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'destination',
        field_name: 'DESTINATION',
        checked: true,
        order_by: 'destination__current_version__localization__name',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'country',
        field_name: 'COUNTRY',
        checked: true,
        order_by: 'destination__current_version__country__international_name',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'categories',
        field_name: 'CATEGORIES',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'online-to',
        field_name: 'VISIBILITY',
        checked: true,
        order_by: '',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'online-b2b',
        field_name: 'AGENCY_VISIBILITY',
        checked: true,
        order_by: '',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'created-date',
        field_name: 'CREATED_DATE',
        checked: false,
        order_by: 'created_date',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'created-by',
        field_name: 'CREATED_BY',
        checked: false,
        order_by: 'creator__username',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'modified-date',
        field_name: 'MODIFIED_DATE',
        checked: true,
        order_by: 'modified_date',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'modified-by',
        field_name: 'MODIFIED_BY',
        checked: true,
        order_by: 'last_author__username',
        fixed_width: true,
        last_updated: false
    }],
    contract_create: false,
    contract_contract: null,
    contract_contract_error: '',
    contract_provider: null,
    count: 0,
    create: false,
    edit: null,
    edit_step: 'INFO',
    error: '',
    editor_pictures: null,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    circuit_name_locales: {},
    filter_country: null,
    filter_categories: null,
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_destination: null,
    filter_source: 'all',
    filter_providers: [],
    filter_languages: [],
    filter_last_author: '',
    filter_reference: '',
    filter_modified_date_gte: null,
    forfait_price: false,
    flight_included: true,
    filter_modified_date_lte: null,
    filter_open: false,
    filter_period_type: 0,
    filter_period: 'CREATION',
    filter_status: 'ALL',
    filter_type: 'ALL',
    destination: null,
    type: null,
    categories: null,
    sale_pitch_locales: {},
    reference: '',
    external_reference: '',
    is_protected_description: false,
    display_for_package: false,
    display_for_trip_plan: true,
    duration_days: 0,
    duration_nights: 0,
    limit: 50,
    list: null,
    name_error: '',
    name: '',
    order_by: 'name',
    order: 'asc',
    page: 0,
    preferences: [],
    refresh: false,
    running: false,
    search: '',
    circuit_pictures: [],
    circuit_pictures_order: false,
    circuit: null,
    providers: [],
    contract_selected: null,
    accomoDation: null,
    flight: null,
    car: null,
    transfers: null,
    trip: [],
    manualProducts: null,
    customProducts: null,
    prices: null,
    prices_master: null,
    duplicate_circuit: null
};

const updateCircuitPeriodProperty = (value: any, price_index: number, index: number, state: CircuitState, key: string) => {
    const room_prices = [...state.prices_master];
    const price = { ...room_prices[price_index] };
    const periods = [...price.periods];
    const period = { ...periods[index] };
    period[key] = value;
    periods[index] = period;
    price.periods = periods;
    room_prices[price_index] = price;
    return { ...state, prices_master: room_prices };
};

export default (state: CircuitState = initialState, action: CircuitActionTypes | ProfileActionTypes | PictureActionTypes): CircuitState => {
    switch (action.type) {
        case ADD_PREFERENCES: {
            return { ...state, preferences: [...state.preferences, ...action.payload] };
        }
        case ADD_CUSTOM_PICTURES: {
            return { ...state, circuit_pictures: [...state.circuit_pictures, ...action.payload] };
        }
        case ADD_CIRCUIT_PERIOD: {
            const room_prices = [...state.prices_master];
            const price = { ...room_prices[action.payload] };
            const periods = [...price.periods];
            periods.push({
                id: periods.length + 1,
                start_date: null,
                start_date_error: '',
                end_date: null,
                end_date_error: '',
                start_days: [0, 1, 2, 3, 4, 5, 6],
                start_days_error: '',
            });
            price.periods = periods;
            room_prices[action.payload] = price;
            return { ...state, prices_master: room_prices };
        }
        case DELETE_CIRCUIT_PICTURE: {
            const circuit_pictures = [...state.circuit_pictures];
            circuit_pictures.splice(action.payload, 1);
            return { ...state, circuit_pictures: circuit_pictures };
        }
        case MOVE_DOWN_COLUMN: {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case MOVE_UP_COLUMN: {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case RESET_ADD_CIRCUIT: {
            return {
                ...state,
                cancel_modal: false,
                edit_step: 'INFO',
                circuit_name_locales: {},
                destination: null,
                type: null,
                reference: '',
                external_reference: '',
                is_protected_description: false,
                display_for_package: false,
                display_for_trip_plan: true,
                categories: null,
                sale_pitch_locales: {},
                duration_days: 0,
                duration_nights: 0,
                circuit_pictures: [],
                circuit_pictures_order: false
            };
        }
        case RESET_FILTERS: {
            return {
                ...state,
                filter_categories: null,
                filter_created_date_gte: null,
                filter_created_date_lte: null,
                filter_creator: '',
                filter_destination: null,
                filter_source: "all",
                filter_providers: [],
                filter_languages: [],
                filter_last_author: '',
                filter_reference: '',
                filter_modified_date_gte: null,
                filter_modified_date_lte: null,
                filter_period: 'CREATION',
                filter_period_type: 0,
                filter_status: 'ALL',
                filter_type: 'ALL'
            };
        }
        case SET_CIRCUIT: {
            const { current_locale, circuit } = action.payload;
            if(circuit) {
                const circuit_name_locales: {} & { [index: string]: never } = {};
                let has_french = false;
                const providers = [...circuit.contracts];
                providers.map(provider => {
                    provider.contracts = [];
                    const contracts = [...circuit.contracts];
                    for (let i = 0; i < contracts.length; i++) {
                        if (provider.id === contracts[i].provider.id) {
                            provider.contracts.push({ ...contracts[i] });
                        }
                    }
                });
    
                circuit.localization.map((locale: CircuitLocalized) => {
                    if (locale.name) {
                        has_french = true;
                    }
                    circuit_name_locales[locale.locale] = locale.name;
                });
                if (!has_french) {
                    circuit_name_locales[current_locale] = circuit.name || '';
                }
                return {
                    ...state,
                    circuit: {...circuit, guide_languages: circuit ?? []},
                    duration_days: circuit.duration_day,
                    duration_nights: circuit.duration_night,
                    reference: circuit.reference,
                    external_reference: circuit.external_reference,
                    is_protected_description: circuit.is_protected_description,
                    display_for_package: circuit.display_for_package,
                    display_for_trip_plan: circuit.display_for_trip_plan,
                    categories: circuit.category,
                    circuit_pictures: circuit.pictures,
                    type: circuit.type,
                    circuit_name_locales: circuit_name_locales,
                    forfait_price: circuit.forfait_price,
                    flight_included: circuit.flight_included,
                    trip: circuit.trip.data,
                    contract_selected: state.circuit?.id !== circuit.id ?
                        null :
                        state.contract_selected,
                    providers: providers,
                };
            } else {
                return {
                    ...state,
                    circuit: null,
                    circuit_name_locales: {},
                    destination: null,
                    type: null,
                    reference: '',
                    external_reference: '',
                    is_protected_description: false,
                    display_for_package: false,
                    display_for_trip_plan: true,
                    categories: null,
                    sale_pitch_locales: {},
                    duration_days: 0,
                    duration_nights: 0,
                    circuit_pictures: [],
                    flight_included: false,
                    trip: [],
                    contract_selected: null,
                    providers: []
                };
            }
        }
        case SET_ACCOMODATION: {
            return { ...state, accomoDation: action.payload };

        }
        case SET_MANUAL_PRODUCTS: {
            return { ...state, manualProducts: action.payload };
        }
        case SET_CIRCUIT_PRICES: {
            if (action.payload === false) {
                return { ...state, prices: null };
            } else {
                return { ...state, prices: action.payload };
            }
        }
        case SET_CUSTOM_PRODUCTS: {
            return { ...state, customProducts: action.payload };
        }
        case SET_FLIGHT: {
            return { ...state, flight: action.payload };
        }
        case SET_CAR: {
            return { ...state, car: action.payload };
        }
        case SET_TRANSFERS: {
            return { ...state, transfers: action.payload };
        }
        case SET_CIRCUIT_PREFERENCES: {
            return { ...state, preferences: action.payload };
        }
        case SET_EDIT_STEP: {
            return { ...state, edit_step: action.payload };
        }
        case SET_EDIT: {
            return { ...state, edit: action.payload };
        }
        case SET_MASTER_PRICE: {
            if (action.payload === false) {
                return { ...state, prices_master: null };
            } else {
                return { ...state, prices_master: action.payload };
            }
        }
        case SET_PRODUCT_PERIOD: {
            return { ...state, product_period: action.payload };
        }
        case SET_PRICES_FLIGHT: {
            return { ...state, prices_flight: action.payload };
        }
        case SET_PRICES_TERRESTRIAL: {
            return { ...state, prices_terrestrial: action.payload };
        }
        case SET_PICTURE: {
            if (action.payload !== true && action.payload !== false) {
                const circuit_pictures = [...state.circuit_pictures];
                for (let i = 0; i < circuit_pictures.length; i++) {
                    if (circuit_pictures[i].id === action.payload.id) {
                        circuit_pictures[i] = { ...action.payload };
                        break;
                    }
                }
                const circuitRootPictures = [...state.circuit?.pictures ?? []];
                for (let i = 0; i < circuitRootPictures.length; i++) {
                    if (circuitRootPictures[i]?.id === action.payload.id) {
                        circuitRootPictures[i] = { ...action.payload };
                        break;
                    }
                }
                return {
                    ...state,
                    circuit: state.circuit ?
                        {
                            ...state.circuit,
                            map_picture: state.circuit.map_picture?.id === action.payload.id ?
                                action.payload :
                                state.circuit.map_picture,
                            pictures: circuitRootPictures
                        } :
                        null,
                    circuit_pictures: circuit_pictures
                };
            }
            return state;
        }
        case SET_EXPORT_TYPE: {
            return { ...state, export_type: action.payload };
        }
        case SET_ERRORS: {
            return { ...state, error: action.payload };
        }
        case TOGGLE_CONTRACT_CREATE: {
            return { ...state, contract_create: !state.contract_create, contract_contract: null, contract_contract_error: '', contract_provider: null };
        }
        case SET_LIMIT: {
            return { ...state, list: null, limit: action.payload };
        }
        case SET_LIST: {
            const { count, results } = action.payload;
            return { ...state, count: count, list: results, running: false };
        }
        case SET_ORDER_BY: {
            return { ...state, list: null, order_by: action.payload, order: state.order_by === action.payload && state.order === 'asc' ? 'desc' : 'asc' };
        }
        case SET_PAGE: {
            return { ...state, list: null, page: action.payload };
        }
        case SET_PREFERENCES: {
            const preferences = action.payload;
            const new_columns: Column[] = [];
            let new_order_by = '-created_date';
            let new_order: 'asc' | 'desc' = 'asc';
            const field_name: string[] = [];
            const circuit_preferences: Preferences[] = [];
            preferences.map(preference => {
                if (preference.module === 'CIRCUIT') {
                    if (!field_name.includes(preference.field_name)) {
                        switch (preference.field_name) {
                            case 'NAME':
                                new_columns.push({ label: 'name', field_name: preference.field_name, checked: preference.active, order_by: 'name', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'name' : new_order_by;
                                break;
                            case 'PROVIDERS':
                                new_columns.push({ label: 'providers', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                break;
                            case 'LANGUAGES':
                                new_columns.push({ label: 'languages', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                break;
                            case 'SOURCE':
                                new_columns.push({ label: 'source', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                break;
                            case 'REFERENCE':
                                new_columns.push({ label: 'reference', field_name: preference.field_name, checked: preference.active, order_by: 'reference', fixed_width: false, last_updated: false });
                                break;
                            case 'TYPE':
                                new_columns.push({ label: 'type', field_name: preference.field_name, checked: preference.active, order_by: 'type', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'type' : new_order_by;
                                break;
                            case 'DESTINATION':
                                new_columns.push({ label: 'destination', field_name: preference.field_name, checked: preference.active, order_by: 'destination__current_version__localization__name', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'destination__current_version__localization__name' : new_order_by;
                                break;
                            case 'COUNTRY':
                                new_columns.push({ label: 'country', field_name: preference.field_name, checked: preference.active, order_by: 'destination__current_version__country__international_name', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'destination__current_version__country__international_name' : new_order_by;
                                break;
                            case 'CATEGORIES':
                                new_columns.push({ label: 'categories', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? '' : new_order_by;
                                break;
                            case 'VISIBILITY':
                                new_columns.push({ label: 'online-to', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: true, last_updated: false });
                                break;
                            case 'AGENCY_VISIBILITY':
                                new_columns.push({ label: 'online-b2b', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: true, last_updated: false });
                                break;
                            case 'CREATED_DATE':
                                new_columns.push({ label: 'created-date', field_name: preference.field_name, checked: preference.active, order_by: 'created_date', fixed_width: true, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'created_date' : new_order_by;
                                break;
                            case 'CREATED_BY':
                                new_columns.push({ label: 'created-by', field_name: preference.field_name, checked: preference.active, order_by: 'creator__username', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'creator__username' : new_order_by;
                                break;
                            case 'MODIFIED_DATE':
                                new_columns.push({ label: 'modified-date', field_name: preference.field_name, checked: preference.active, order_by: 'modified_date', fixed_width: true, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'modified_date' : new_order_by;
                                break;
                            case 'MODIFIED_BY':
                                new_columns.push({ label: 'modified-by', field_name: preference.field_name, checked: preference.active, order_by: 'last_author__username', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'last_author__username' : new_order_by;
                                break;
                            default:
                                break;
                        }
                        circuit_preferences.push(preference);
                        field_name.push(preference.field_name);
                        new_order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : new_order;
                    }
                }
            });
            if (new_columns.length !== state.columns.length) {
                state.columns.map(column => {
                    let added = false;
                    for (let i = 0; i < new_columns.length; i++) {
                        if (new_columns[i].label === column.label) {
                            added = true;
                            break;
                        }
                    }
                    if (!added) {
                        new_columns.push({ ...column });
                    }
                });
            }
            return { ...state, preferences: circuit_preferences, columns: new_columns, order_by: new_order_by, order: new_order };
        }
        case SET_RUNNING: {
            return { ...state, running: action.payload };
        }
        case SET_SEARCH: {
            return { ...state, list: null, page: 0, search: action.payload };
        }
        case TOGGLE_COLUMN: {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case SET_COLUMNS: {
            return {...state, columns: action.payload};
        }
        case TOGGLE_CREATE: {
            return { ...state, create: !state.create };
        }
        case TOGGLE_CANCEL_MODAL: {
            return { ...state, cancel_modal: !state.cancel_modal };
        }
        case TOGGLE_EXPORT: {
            return { ...state, export_list: action.payload };
        }
        case TOGGLE_EXPORT_FULL: {
            return { ...state, export_full: !state.export_full };
        }
        case TOGGLE_FILTERS: {
            return { ...state, filter_open: !state.filter_open };
        }
        case TOGGLE_REFRESH: {
            return { ...state, refresh: !state.refresh };
        }
        case TOGGLE_CIRCUIT_PICTURES_ORDER: {
            return { ...state, circuit_pictures_order: !state.circuit_pictures_order };
        }
        case UPDATE_CIRCUIT_PICTURE_ORDER: {
            const { drag_index, hover_index } = action.payload;
            const circuit_pictures = [...state.circuit_pictures];
            circuit_pictures.splice(drag_index, 1);
            circuit_pictures.splice(hover_index, 0, state.circuit_pictures[drag_index]);
            return { ...state, circuit_pictures: circuit_pictures };
        }
        case UPDATE_PRODUCTS: {
            return {
                ...state,
                accomoDation: state.accomoDation?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                transfers: state.transfers?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                customProducts: state.customProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                manualProducts: state.manualProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                car: state.car?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                flight: state.flight?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
            };
        }
        case UPDATE_PRODUCTS_STACK: {
            return {
                ...state,
                accomoDation: state.accomoDation?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                transfers: state.transfers?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                customProducts: state.customProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                manualProducts: state.manualProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                car: state.car?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                flight: state.flight?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
            };
        }
        case UPDATE_PERIOD_START_DATE: {
            const { value, price_index, period_index } = action.payload;
            return updateCircuitPeriodProperty(value, price_index, period_index, state, 'start_date');
        }
        case UPDATE_PERIOD_END_DATE: {
            const { value, price_index, period_index } = action.payload;
            return updateCircuitPeriodProperty(value, price_index, period_index, state, 'end_date');
        }
        case UPDATE_PERIOD_DAYS: {
            const { value, price_index, period_index } = action.payload;
            return updateCircuitPeriodProperty(value, price_index, period_index, state, 'start_days');
        }
        case DELETE_CIRCUIT_PERIOD: {
            const { price_index, period_index } = action.payload;
            const room_prices = [...state.prices_master];
            const price = { ...room_prices[price_index] };
            console.log('delete circuit period: ', room_prices);
            const periods = [...price.periods];
            periods.splice(period_index, 1);
            price.periods = periods;
            room_prices[price_index] = price;
            return { ...state, prices_master: room_prices };
        }
        case SET_DUPLICATE_CIRCUIT : {
            return { ...state, duplicate_circuit: action.payload};
        }
        case DELETE_CIRCUIT_PRICE: {
            const room_prices = [...state.prices_master];
            room_prices.splice(action.payload, 1);
            return { ...state, prices_master: room_prices };
        }
        case UPDATE_PRICE_TYPE: {
            return { ...state, forfait_price: action.payload };
        }
        case UPDATE_RETURN_FLIGHT: {
            return { ...state, flight_included: action.payload };
        }
        case UPDATE_CONTRACT_SELECTED: {
            return { ...state, contract_selected: action.payload, extra_list: [] };
        }
        case UPDATE_CIRCUIT_NAME_LOCALE: {
            const { id, value } = action.payload;
            const circuit_name_locales = { ...state.circuit_name_locales };
            circuit_name_locales[id] = value;
            return { ...state, circuit_name_locales: circuit_name_locales };
        }
        case UPDATE_CIRCUIT_NAME_LOCALES: {
            return { ...state, circuit_name_locales: action.payload };
        }
        case UPDATE_FILTER_CATEGORIES: {
            return { ...state, filter_categories: action.payload };
        }
        case UPDATE_FILTER_COUNTRY: {
            return { ...state, filter_country: action.payload };
        }
        case UPDATE_FILTER_CREATED_DATE_GTE: {
            return { ...state, filter_created_date_gte: action.payload };
        }
        case UPDATE_FILTER_CREATED_DATE_LTE: {
            return { ...state, filter_created_date_lte: action.payload };
        }
        case UPDATE_FILTER_CREATOR: {
            return { ...state, filter_creator: action.payload };
        }
        case UPDATE_FILTER_DESTINATION: {
            return { ...state, filter_destination: action.payload };
        }
        case UPDATE_FILTER_SOURCE: {
            return { ...state, filter_source: action.payload };
        }
        case UPDATE_FILTER_PROVIDERS: {
            return { ...state, filter_providers: action.payload };
        }
        case UPDATE_FILTER_LANGUAGES: {
            return { ...state, filter_languages: action.payload };
        }
        case UPDATE_FILTER_LAST_AUTHOR: {
            return { ...state, filter_last_author: action.payload };
        }
        case UPDATE_FILTER_REFERENCE: {
            return { ...state, filter_reference: action.payload };
        }
        case UPDATE_FILTER_MODIFIED_DATE_GTE: {
            return { ...state, filter_modified_date_gte: action.payload };
        }
        case UPDATE_FILTER_MODIFIED_DATE_LTE: {
            return { ...state, filter_modified_date_lte: action.payload };
        }
        case UPDATE_FILTER_PERIOD: {
            const period = action.payload;
            return {
                ...state,
                filter_period: period,
                filter_period_type: 0,
                filter_created_date_gte: period === 'MODIFICATION' ? null : state.filter_created_date_gte,
                filter_created_date_lte: period === 'MODIFICATION' ? null : state.filter_created_date_lte,
                filter_modified_date_gte: period === 'CREATION' ? null : state.filter_modified_date_gte,
                filter_modified_date_lte: period === 'CREATION' ? null : state.filter_modified_date_lte,
            };
        }
        case UPDATE_FILTER_PERIOD_TYPE: {
            return { ...state, filter_period_type: action.payload };
        }
        case UPDATE_FILTER_STATUS: {
            return { ...state, filter_status: action.payload };
        }
        case UPDATE_FILTER_TYPE: {
            return { ...state, filter_type: action.payload };
        }
        case UPDATE_DESTINATION: {
            return { ...state, destination: action.payload };
        }
        case UPDATE_TRIP: {
            return { ...state, circuit: state.circuit ? { ...state.circuit, trip: action.payload } : null };
        }
        case UPDATE_TYPE: {
            return { ...state, type: action.payload };
        }
        case UPDATE_REFERENCE: {
            return { ...state, reference: action.payload };
        }
        case UPDATE_EXTERNAL_REFERENCE: {
            return { ...state, external_reference: action.payload };
        }
        case SET_IS_PROTECTED_DESCRIPTION: {
            return { ...state, is_protected_description: action.payload };
        }
        case UPDATE_DISPLAY_FOR_PACKAGE: {
            return { ...state, display_for_package: action.payload };
        }
        case UPDATE_DISPLAY_FOR_TRIP_PLAN: {
            return { ...state, display_for_trip_plan: action.payload };
        }
        case UPDATE_PROVIDER: {
            return { ...state, contract_provider: action.payload, contract_contract: null, contract_contract_error: '' };
        }
        case UPDATE_CONTRACT: {
            return { ...state, contract_contract: action.payload, contract_contract_error: '' };
        }
        case UPDATE_CATEGORIES: {
            return { ...state, categories: action.payload };
        }
        case UPDATE_DURATION_DAYS: {
            return { ...state, duration_days: action.payload };
        }
        case UPDATE_DURATION_NIGHTS: {
            return { ...state, duration_nights: action.payload };
        }
        case UPDATE_SALE_PITCH_LOCALE: {
            const { id, value } = action.payload;
            const sale_pitch_locales = { ...state.sale_pitch_locales };
            sale_pitch_locales[id] = value;
            return { ...state, sale_pitch_locales: sale_pitch_locales };
        }
        case UPDATE_LIST_ITEM_VISIBILITY: {
            if (state.list !== null) {
                const list = [...state.list];
                for (let i = 0; i < list.length; i++) {
                    if (list[i].id === action.payload.id) {
                        list[i].visibility = action.payload.visibility;
                        break;
                    }
                }
                return { ...state, list: list };
            }
            return state;
        }
        case UPDATE_SELECTED_CIRCUIT: {
            return { ...state, selected_circuit: action.payload };
        }
        case UPDATE_MANY_CIRCUIT_VISIBILITY: {
            return { ...state, list: state.list?.map(circuit => action.payload.circuits_id.includes(circuit.id)? { ...circuit, visibility: action.payload.visibility }: circuit ) ?? null};
        }
        default:
            return state;
    }
};

export type InputId = number | GeneratedId

type GeneratedId = `new-${number}`

export type PriceRangePeriod = Omit<PricePeriod, 'id' | 'variant' | 'for_promo_only' | 'contract' | 'trip_version'> & {
    id: InputId,
}

export type PriceRangeTerrestrialPrice = Omit<TerrestrialPrice, 'id' | 'periods' | 'contract'> & {
    id: InputId,
}

export type PriceRangeFlightPrice = Omit<FlightPrice, 'id' | 'periods' | 'contract'> & {
    id: InputId,
}

export type PriceRangePromotion = Omit<Promotion, 'id' | 'periods' | 'contract'> & {
    id: InputId
}

export type PriceRange = {
    id: InputId,
    periods: PriceRangePeriod[],
    terrestrialPrices: PriceRangeTerrestrialPrice[],
    flightPrices: PriceRangeFlightPrice[],
    promotions: PriceRangePromotion[]
}

export type PricesTablePeriod = {
    id: InputId,
    startDate: string | Date | null,
    endDate: string | Date | null,
    startDateError?: string,
    endDateError?: string,
}

export type PricesTableAirportsList = {
    id: InputId,
    airports: Airport[]
}

export type PricesTableItemPrice = {
    id: InputId,
    periodId: InputId,
    airportsListId?: InputId,
    terrestrialPrice: string,
    flightPrice: string
}

export type VersionInfo = CircuitSliceState['pricesEdit'][`${number}-${number}`]['inputs'][0]['versionInfo']

export type CircuitSliceState = {
    flight_params_list: FlightParams[],
    first_preference_loaded: boolean,
    flight_params_list_configuration: FlightParamsListConfiguration,
    flight_params_list_loading: boolean,
    flight_params_list_count: number,
    flight_params_list_criteria: FlightParamsListCriteria,
    flight_params: FlightParams | null,
    refresh_flight_params: boolean,
    edit_flight_params_step: string,
    pricesEdit: {
        // circuitId-contractId
        [key: `${number}-${number}`]: {
            state: 'loading' | 'success' | 'error',
            parameters: {
                datesMode: PricesParametersDatesMode,
                datesSelection: PricesParametersDatesSelection,
            },
            inputs: {
                versionInfo: { version: number, variant: number },
                priceRanges: PriceRange[]
            }[],
            periods: PricePeriod[],
            terrestrialPrices: TerrestrialPrice[],
            flightPrices: FlightPrice[],
            modelPrices: ModelPrice[],
            promotions: Promotion[]
        }
    },
    pricesTableEdit: {
        // circuitId-contractId
        [key: `${number}-${number}`]: {
            state: 'loading' | 'success' | 'error',
            parameters: {
                datesMode: PricesParametersDatesMode,
                datesSelection: PricesParametersDatesSelection,
            },
            inputs: {
                versionInfo: VersionInfo,
                periods: PricesTablePeriod[],
                airportsList: PricesTableAirportsList[],
                prices: PricesTableItemPrice[]
            }[],
            table: {
                id: number,
                contract: Contract | null,
                airports: Airport[],
                price_xml: string,
                price_xml_purchase: string,
                price_flight: string,
                price_flight_purchase: string,
                start_date: string,
                end_date: string,
                trip_version: number,
                variant: number,
            }[]
        }
    },
    itinerary: {
        [circuit: number]: {
            state: 'loading'
        } | {
            state: 'success',
            data: {
                [version: number]: Itinerary[]
            }
        } | {
            state: 'error',
            error: Error
        }
    },
    itineraryByDay: {
        [circuit: number]: {
            state: 'loading'
        } | {
            state: 'success',
            data: ItineraryByDay[]
        } | {
            state: 'error',
            error: Error
        }
    },
    products: ListNetworkData,
    circuit_locale: number,
    currency_list: Currency[]
    cancelationRules: ListNetworkData,
    globalCancelationRules: ListNetworkData
    refresh: boolean;
    circuit_info_errors: CircuitInfoErrors;
}

const circuitSliceInitialValue: CircuitSliceState = {
    flight_params_list: [],
    flight_params_list_configuration: EMPTY_FLIGHT_PARAMS_LIST_CONFIGURATION,
    flight_params_list_loading: false,
    flight_params_list_count: 0,
    flight_params_list_criteria: EMPTY_FLIGHT_PARAMS_LIST_CRITERIA,
    flight_params: null,
    refresh_flight_params: false,
    edit_flight_params_step: '',
    pricesEdit: {},
    pricesTableEdit: {},
    itinerary: {},
    itineraryByDay: {},
    products: { state: 'loading' },
    circuit_locale: 0,
    currency_list: [],
    cancelationRules: { state: 'loading' },
    globalCancelationRules: { state: 'loading' },
    refresh: false,
    first_preference_loaded: false,
    circuit_info_errors: EMPTY_CIRCUIT_INFO_ERRORS
};


export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const updateCircuitInfoErrorsReducer = (state: CircuitState, action: PayloadAction<CircuitInfoErrors>): CircuitSliceState => ({
    ...state, circuit_info_errors: action.payload
});
const addFlightParamsListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, flight_params_list_configuration: { ...state.flight_params_list_configuration, preferences: [...state.flight_params_list_configuration.preferences, ...action.payload] }
});

const setFlightParamsListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, flight_params_list_configuration: { ...state.flight_params_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => {
    const { preferences, columns, order_by, order } = setFlightParamsPreference(action.payload, state.flight_params_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        flight_params_list_configuration: { ...state.flight_params_list_configuration, preferences, columns },
        flight_params_list_criteria: { ...state.flight_params_list_criteria, order, order_by }
    };
};

const setColumnsReducer = (state: CircuitSliceState, action: PayloadAction<Column[]>): CircuitSliceState => ({
    ...state, flight_params_list_configuration: { ...state.flight_params_list_configuration, columns: action.payload },
});

const toggleColumnReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => {
    const columns = cloneDeep([...state.flight_params_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, flight_params_list_configuration: { ...state.flight_params_list_configuration, columns } };
};

const updateFlightParamsListReducer = (state: CircuitSliceState, action: PayloadAction<FlightParams[]>): CircuitSliceState => ({
    ...state, flight_params_list: action.payload.map(params => ({
        ...params,
        providers: params.providers || [],
        allowed_client_type: params.allowed_client_type || [],
        allowed_airline: params.allowed_airline || [],
        disallowed_airline: params.disallowed_airline || [],
        number_of_bags: params.number_of_bags || [],
        allowed_stopovers_airport: params.allowed_stopovers_airport || [],
        disallowed_stopovers_airport: params.disallowed_stopovers_airport || [],
        allowed_departure_airport: params.allowed_departure_airport || [],
        disallowed_departure_airport: params.disallowed_departure_airport || [],
        allowed_arrival_airport: params.allowed_arrival_airport || [],
        disallowed_arrival_airport: params.disallowed_arrival_airport || [],
        period: params.period || [],
        departure_dow: params.departure_dow || [],
        arrival_dow: params.arrival_dow || [],
        cabin: params.cabin || [],
        tariff_letters: params.tariff_letters || []
    }))
});
const updateFlightParamsListCriteriaReducer = (state: CircuitSliceState, action: PayloadAction<FlightParamsListCriteria>): CircuitSliceState => ({
    ...state, flight_params_list_criteria: action.payload
});
const updateFlightParamsReducer = (state: CircuitSliceState, action: PayloadAction<FlightParams>): CircuitSliceState => ({
    ...state,
    flight_params: {
        ...action.payload,
        assigned_trip: action.payload.assigned_trip ?? 'ALL',
        max_stops: action.payload.max_stops ?? '',
        max_hour_spent_on_stopover: action.payload.max_hour_spent_on_stopover ?? '',
        start_arrival_time: action.payload.start_arrival_time ?? '',
        end_arrival_time: action.payload.end_arrival_time ?? '',
        start_departure_time: action.payload.start_departure_time ?? '',
        end_departure_time: action.payload.end_departure_time ?? '',
        start_arrival_time_inbound: action.payload.start_arrival_time_inbound ?? '',
        end_arrival_time_inbound: action.payload.end_arrival_time_inbound ?? '',
        start_departure_time_inbound: action.payload.start_departure_time_inbound ?? '',
        end_departure_time_inbound: action.payload.end_departure_time_inbound ?? '',
        allow_number_of_allotment: action.payload.allow_number_of_allotment ?? '',
        flight_duration_min: action.payload.flight_duration_min ?? '',
        flight_duration_max: action.payload.flight_duration_max ?? '',
        allowed_client_type: action.payload.allowed_client_type ?? [],
        period: action.payload.period.map((period) => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })),
        providers: action.payload.providers || [],
        allowed_airline: action.payload.allowed_airline || [],
        disallowed_airline: action.payload.disallowed_airline || [],
        number_of_bags: action.payload.number_of_bags || [],
        allowed_stopovers_airport: action.payload.allowed_stopovers_airport || [],
        disallowed_stopovers_airport: action.payload.disallowed_stopovers_airport || [],
        allowed_departure_airport: action.payload.allowed_departure_airport || [],
        disallowed_departure_airport: action.payload.disallowed_departure_airport || [],
        allowed_arrival_airport: action.payload.allowed_arrival_airport || [],
        disallowed_arrival_airport: action.payload.disallowed_arrival_airport || [],
        departure_dow: action.payload.departure_dow || [],
        arrival_dow: action.payload.arrival_dow || [],
        cabin: action.payload.cabin || [],
        tariff_letters: action.payload.tariff_letters || []
    }
});
const updateFlightParamsListLoadingReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, flight_params_list_loading: action.payload
});
const updateFlightParamsListCountReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => ({
    ...state, flight_params_list_count: action.payload
});

export const circuitSlice = createSlice({
    name: 'circuitSlice',
    initialState: circuitSliceInitialValue,
    reducers: {
        toggleRefreshFlightParams: (state: CircuitSliceState) => ({ ...state, refresh_flight_params: !state.refresh_flight_params }),
        setFlightParams: (state: CircuitSliceState, action: PayloadAction<FlightParams | null>) => ({ ...state, flight_params: action.payload ? {
            ...action.payload,
            assigned_trip: action.payload.assigned_trip ?? 'ALL',
            max_stops: action.payload.max_stops ?? '',
            max_hour_spent_on_stopover: action.payload.max_hour_spent_on_stopover ?? '',
            start_arrival_time: action.payload.start_arrival_time ?? '',
            end_arrival_time: action.payload.end_arrival_time ?? '',
            start_departure_time: action.payload.start_departure_time ?? '',
            end_departure_time: action.payload.end_departure_time ?? '',
            start_arrival_time_inbound: action.payload.start_arrival_time_inbound ?? '',
            end_arrival_time_inbound: action.payload.end_arrival_time_inbound ?? '',
            start_departure_time_inbound: action.payload.start_departure_time_inbound ?? '',
            end_departure_time_inbound: action.payload.end_departure_time_inbound ?? '',
            allow_number_of_allotment: action.payload.allow_number_of_allotment ?? '',
            flight_duration_min: action.payload.flight_duration_min ?? '',
            flight_duration_max: action.payload.flight_duration_max ?? '',
            allowed_client_type: action.payload.allowed_client_type ?? [],
            period: action.payload.period.map((period) => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null }))
        } : null }),
        setEditFlightParamsSetps: (state: CircuitSliceState, action: PayloadAction<string>) => ({ ...state, edit_flight_params_step: action.payload }),
        addPricesEditPriceRange: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.priceRanges.push({
                    id: generateId(),
                    periods: [
                        {
                            id: generateId(),
                            start_date: new Date().toISOString(),
                            end_date: new Date().toISOString(),
                            start_days: [0, 1, 2, 3, 4, 5, 6]
                        }
                    ],
                    terrestrialPrices: [],
                    flightPrices: [],
                    promotions: []
                });
            } else {
                state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.push({
                    versionInfo: action.payload.versionInfo,
                    priceRanges: [{
                        id: generateId(),
                        periods: [
                            {
                                id: generateId(),
                                start_date: new Date().toISOString(),
                                end_date: new Date().toISOString(),
                                start_days: [0, 1, 2, 3, 4, 5, 6]
                            }
                        ],
                        terrestrialPrices: [],
                        flightPrices: [],
                        promotions: []
                    }]
                });
            }
        },
        addPricesEditPeriod: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                datesMode: PricesParametersDatesMode
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                const date = priceRange.periods.length > 0 ? new Date(priceRange.periods[priceRange.periods.length - 1].end_date): new Date();
                let day = getDay(date) - 1;
                day = day < 0 ? 6 : day;
                priceRange.periods.push({
                    id: generateId(),
                    start_date: date.toISOString(),
                    end_date: action.payload.datesMode === 'FIXED' ?
                        date.toISOString() :
                        addDays(date, 1).toString(),
                    start_days: [day]
                });
            }
        },
        addPricesEditTerrestrialPrice: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                model: ModelPrice
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.terrestrialPrices.push({
                    id: generateId(),
                    is_price_reference: false,
                    model: action.payload.model,
                    values: action.payload.model.variable.map((variable) => ({
                        variable: variable as 'AGE' | 'NUMBER_OF_PERSONS' | 'NUMBER_OF_PERSONS_PRICE',
                        value: [0, 0]
                    })),
                    price: ''
                });
            }
        },
        addPricesEditFlightPrice: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId?: InputId,
                airport?: Airport
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            const price = priceRange?.flightPrices.find((item) => item.id === action.payload.priceId);
            if (price && action.payload.airport) {
                price.airports.push(action.payload.airport);
            } else {
                priceRange?.flightPrices.push({
                    id: action.payload.priceId ?? generateId(),
                    airports: action.payload.airport ? [action.payload.airport] : [],
                    price: ''
                });
            }
        },
        addPricesEditPromotion: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                model: ModelPrice
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.promotions.push({
                    id: generateId(),
                    name: '',
                    is_price_reduction: false,
                    strict_price_model: false,
                    model: action.payload.model,
                    values: action.payload.model.variable.map((variable) => ({
                        variable: variable as 'AGE' | 'NUMBER_OF_PERSONS' | 'NUMBER_OF_PERSONS_PRICE',
                        value: [0, 0]
                    })),
                    price: ''
                });
            }
        },
        addPricesTableEditAirport: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                listId: InputId,
                airport: Airport
            }>
        ): void => {
            const list = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.airportsList.find((item) => {
                return item.id === action.payload.listId;
            });

            if (list) {
                list.airports.push(action.payload.airport);
            }
        },
        addPricesTableEditAirportsList: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.airportsList.push({
                    id: generateId(),
                    airports: []
                });
            } else {
                state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.push({
                    versionInfo: action.payload.versionInfo,
                    periods: [],
                    airportsList: [{
                        id: generateId(),
                        airports: []
                    }],
                    prices: []
                });
            }
        },
        addPricesTableEditPeriod: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.periods.push({
                    id: generateId(),
                    startDate: item.periods.length> 0? item.periods[item.periods.length - 1].endDate :new Date().toISOString(),
                    endDate: item.periods.length> 0? item.periods[item.periods.length - 1].endDate :new Date().toISOString(),
                });
            } else {
                state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.push({
                    versionInfo: action.payload.versionInfo,
                    periods: [{
                        id: generateId(),
                        startDate: new Date().toISOString(),
                        endDate: new Date().toISOString(),
                    }],
                    airportsList: [],
                    prices: []
                });
            }
        },
        deletePricesEditPriceRange: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.priceRanges = item.priceRanges.filter((item) => item.id !== action.payload.priceRangeId);
            }
        },
        deletePricesEditPeriod: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                periodId: InputId
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.periods = priceRange.periods.filter((item) => item.id !== action.payload.periodId);
            }
        },
        deletePricesEditTerrestrialPrice: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId: InputId
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.terrestrialPrices = priceRange.terrestrialPrices.filter((item) => {
                    return item.id !== action.payload.priceId;
                });
            }
        },
        deletePricesEditPromotion: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                promotionId: InputId
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.promotions = priceRange.promotions.filter((item) => {
                    return item.id !== action.payload.promotionId;
                });
            }
        },
        deletePricesEditFlightPrice: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId: InputId
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.flightPrices = priceRange.flightPrices.filter((item) => {
                    return item.id !== action.payload.priceId;
                });
            }
        },
        deletePricesEditAirport: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId: InputId,
                airport: Airport
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            const price = priceRange?.flightPrices.find((item) => item.id === action.payload.priceId);
            if (price) {
                price.airports = price.airports.filter((item) => item.id !== action.payload.airport.id);
            }
        },
        deletePricesTableEditAirport: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                listId: InputId,
                airport: Airport
            }>
        ): void => {
            const list = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.airportsList.find((item) => {
                return item.id === action.payload.listId;
            });
            if (list) {
                list.airports = list.airports.filter((item) => {
                    return item.id !== action.payload.airport.id;
                });
            }
        },
        deletePricesTableEditAirportsList: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                listId: InputId,
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.airportsList = item.airportsList.filter((item) => {
                    return item.id !== action.payload.listId;
                });
                item.prices = item.prices.filter((item) => {
                    return item.airportsListId !== action.payload.listId;
                });
            }
        },
        deletePricesTableEditPeriod: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                periodId: InputId,
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.periods = item.periods.filter((item) => {
                    return item.id !== action.payload.periodId;
                });
                item.prices = item.prices.filter((item) => {
                    return item.periodId !== action.payload.periodId;
                });
            }
        },
        setCancelationRules: (
            state,
            action: PayloadAction<CircuitSliceState['cancelationRules']>
        ): void => {
            state.cancelationRules = action.payload;
        },
        setGlobalCancelationRules: (
            state,
            action: PayloadAction<CircuitSliceState['globalCancelationRules']>
        ): void => {
            state.globalCancelationRules = action.payload;
        },
        setPricesEditValue: <K extends keyof CircuitSliceState['pricesEdit'][`${number}-${number}`]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                key: K,
                value: CircuitSliceState['pricesEdit'][`${number}-${number}`][K]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices[action.payload.key] = action.payload.value;
            } else {
                state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`] = {
                    state: 'loading',
                    parameters: {
                        datesMode: 'FREE',
                        datesSelection: 'DATE_PICKER',
                    },
                    inputs: [],
                    periods: [],
                    terrestrialPrices: [],
                    flightPrices: [],
                    modelPrices: [],
                    promotions: [],
                    [action.payload.key]: action.payload.value
                };
            }
        },
        setPricesEditParameters: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                parameters: CircuitSliceState['pricesEdit'][`${number}-${number}`]['parameters']
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.parameters = action.payload.parameters;
            }
        },
        setPricesEditPriceRanges: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRanges: PriceRange[]
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.priceRanges = action.payload.priceRanges;
            } else {
                state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.push({
                    versionInfo: action.payload.versionInfo,
                    priceRanges: action.payload.priceRanges
                });
            }
        },
        setPricesEditPeriodValue: <K extends keyof PriceRange['periods'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                periodId: InputId,
                key: K,
                value: PriceRange['periods'][0][K]
            }>
        ): void => {
            const period = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((item) => {
                return item.id === action.payload.priceRangeId;
            })?.periods.find((item) => {
                return item.id === action.payload.periodId;
            });
            if (period) {
                if(action.payload.key === 'start_date' || action.payload.key === 'end_date') {
                    period[`${(action.payload.key as unknown as 'start_date' | 'end_date')}_error`] = undefined;
                    period[action.payload.key] = isValid(action.payload.value) ? (action.payload.value as Date)?.toISOString(): action.payload.value;
                } else {
                    period[action.payload.key] = action.payload.value;
                }
            }
        },
        setPricesEditTerrestrialPriceValue: <K extends keyof PriceRange['terrestrialPrices'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                modelId: InputId,
                key: K,
                value: PriceRange['terrestrialPrices'][0][K]
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.terrestrialPrices.find((terrestrialPrice) => {
                return terrestrialPrice.id === action.payload.modelId;
            });
            if (price) {
                price[action.payload.key] = action.payload.value;
            }
        },
        setPricesEditFlightPriceValue: <K extends keyof PriceRange['flightPrices'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId: InputId,
                key: K,
                value: PriceRange['flightPrices'][0][K]
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.flightPrices.find((flightPrice) => {
                return flightPrice.id === action.payload.priceId;
            });
            if (price) {
                price[action.payload.key] = action.payload.value;
            }
        },
        setPricesEditPromotionValue: <K extends keyof PriceRange['promotions'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                promotionId: InputId,
                key: K,
                value: PriceRange['promotions'][0][K]
            }>
        ): void => {
            const promotion = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.promotions.find((promotion) => {
                return promotion.id === action.payload.promotionId;
            });
            if (promotion) {
                promotion[action.payload.key] = action.payload.value;
            }
        },
        setPricesTableEditValue: <K extends keyof CircuitSliceState['pricesTableEdit'][`${number}-${number}`]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                key: K,
                value: CircuitSliceState['pricesTableEdit'][`${number}-${number}`][K]
            }>
        ): void => {
            const contractPrices = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices[action.payload.key] = action.payload.value;
            } else {
                state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`] = {
                    state: 'loading',
                    parameters: {
                        datesMode: 'FREE',
                        datesSelection: 'DATE_PICKER',
                    },
                    inputs: [],
                    table: []
                };
            }
        },
        setPricesTableEditParameters: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                parameters: CircuitSliceState['pricesTableEdit'][`${number}-${number}`]['parameters']
            }>
        ): void => {
            const contractPrices = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.parameters = action.payload.parameters;
            }
        },
        setPricesTableEditPeriodValue: <K extends keyof PricesTablePeriod>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                periodId: InputId,
                key: K,
                value: PricesTablePeriod[K]
            }>
        ): void => {
            const period = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.periods.find((item) => {
                return item.id === action.payload.periodId;
            });
            if (period) {
                if(action.payload.key === 'startDate' || action.payload.key === 'endDate') {
                    period[`${(action.payload.key as unknown as 'startDate' | 'endDate')}Error`] = undefined;
                    period[action.payload.key] = isValid(action.payload.value) ? (action.payload.value as Date)?.toISOString(): action.payload.value;
                } else  {
                    period[action.payload.key] = action.payload.value;
                }
            }
        },
        setPricesTableEditPriceValue: <K extends keyof PricesTableItemPrice>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                periodId: InputId,
                airportsListId?: InputId,
                key: K,
                value: PricesTableItemPrice[K]
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const price = item?.prices.find((price) => {
                return price.periodId === action.payload.periodId &&
                    (!action.payload.airportsListId || price.airportsListId === action.payload.airportsListId);
            });
            if (price) {
                price[action.payload.key] = action.payload.value;
            } else if (item) {
                item.prices.push({
                    id: generateId(),
                    periodId: action.payload.periodId,
                    airportsListId: action.payload.airportsListId,
                    flightPrice: '',
                    terrestrialPrice: '',
                    [action.payload.key]: action.payload.value
                });
            }
        },
        setItinerary: (
            state,
            action: PayloadAction<{
                circuit: number,
                data: CircuitSliceState['itinerary'][0]
            }>
        ): void => {
            state.itinerary[action.payload.circuit] = action.payload.data;
        },
        setItineraryByDay: (
            state,
            action: PayloadAction<{
                circuit: number,
                data: CircuitSliceState['itineraryByDay'][0]
            }>
        ): void => {
            state.itineraryByDay[action.payload.circuit] = action.payload.data;
        },
        setProducts: (
            state,
            action: PayloadAction<CircuitSliceState['products']>
        ): void => {
            state.products = action.payload;
        },
        updateItineraryStepPicture: (
            state,
            action: PayloadAction<Picture>
        ): void => {
            state.itineraryByDay = mapValues(
                state.itineraryByDay,
                (value) => {
                    if (value.state === 'success') {
                        return {
                            ...value,
                            data: value.data.map((item) => {
                                if (item.picture?.id === action.payload.id) {
                                    return {
                                        ...item,
                                        picture: action.payload
                                    };
                                }
                                return item;
                            })
                        };
                    }
                    return value;
                }
            );
        },
        updatePricesEditPeriods: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                periods: PricePeriod[],
                deleted: number[]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.periods = contractPrices.periods.filter((item) => {
                    return !action.payload.deleted.includes(item.id);
                });
                contractPrices.periods = contractPrices.periods.map((item) => {
                    const newPeriod = action.payload.periods.find((newItem) => item.id === newItem.id);
                    if (newPeriod) {
                        return newPeriod;
                    }
                    return item;
                });
                const newPeriods = action.payload.periods.filter((newItem) => {
                    return !contractPrices.periods.map((item) => item.id).includes(newItem.id);
                });
                contractPrices.periods = contractPrices.periods.concat(newPeriods);
            }
        },
        updatePricesEditTerrestrialPrices: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                prices: TerrestrialPrice[],
                deleted: number[]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.terrestrialPrices = contractPrices.terrestrialPrices.filter((item) => {
                    return !action.payload.deleted.includes(item.id);
                });
                contractPrices.terrestrialPrices = contractPrices.terrestrialPrices.map((item) => {
                    const newPrice = action.payload.prices.find((newItem) => item.id === newItem.id);
                    if (newPrice) {
                        return newPrice;
                    }
                    return item;
                });
                const newPrices = action.payload.prices.filter((newItem) => {
                    return !contractPrices.terrestrialPrices.map((item) => item.id).includes(newItem.id);
                });
                contractPrices.terrestrialPrices = contractPrices.terrestrialPrices.concat(newPrices);
            }
        },
        updatePricesEditFlightPrices: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                prices: FlightPrice[],
                deleted: number[]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.flightPrices = contractPrices.flightPrices.filter((item) => {
                    return !action.payload.deleted.includes(item.id);
                });
                contractPrices.flightPrices = contractPrices.flightPrices.map((item) => {
                    const newPrice = action.payload.prices.find((newItem) => item.id === newItem.id);
                    if (newPrice) {
                        return newPrice;
                    }
                    return item;
                });
                const newPrices = action.payload.prices.filter((newItem) => {
                    return !contractPrices.flightPrices.map((item) => item.id).includes(newItem.id);
                });
                contractPrices.flightPrices = contractPrices.flightPrices.concat(newPrices);
            }
        },
        updatePricesEditPromotions: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                promotions: Promotion[],
                deleted: number[]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.promotions = contractPrices.promotions.filter((item) => {
                    return !action.payload.deleted.includes(item.id);
                });
                contractPrices.promotions = contractPrices.promotions.map((item) => {
                    const newPromotion = action.payload.promotions.find((newItem) => item.id === newItem.id);
                    if (newPromotion) {
                        return newPromotion;
                    }
                    return item;
                });
                const newPromotions = action.payload.promotions.filter((newItem) => {
                    return !contractPrices.promotions.map((item) => item.id).includes(newItem.id);
                });
                contractPrices.promotions = contractPrices.promotions.concat(newPromotions);
            }
        },
        updateProducts: (
            state,
            action: PayloadAction<{ ids: number[], lock: boolean }>
        ): void => {
            state.products = state.products.state === 'success' ?
                {
                    ...state.products,
                    data: {
                        ...state.products.data,
                        results: state.products.data.results.map((item: Product) => {
                            if (action.payload.ids.includes(item.id)) {
                                return {
                                    ...item,
                                    deletion_restriction: action.payload.lock
                                };
                            }
                            return item;
                        })
                    }
                } :
                state.products;
        },
        updateProductsStack: (
            state,
            action: PayloadAction<{ ids: number[], is_stack_price: boolean }>
        ): void => {
            state.products = state.products.state === 'success' ?
                {
                    ...state.products,
                    data: {
                        ...state.products.data,
                        results: state.products.data.results.map((item: Product) => {
                            if (action.payload.ids.includes(item.id)) {
                                return {
                                    ...item,
                                    is_stack_price: action.payload.is_stack_price
                                };
                            }
                            return item;
                        })
                    }
                } :
                state.products;
        },
        updateItineraryByDay: (
            state,
            action: PayloadAction<{
                circuit: number,
                data: ItineraryByDay[]
            }>
        ): void => {
            state.itineraryByDay[action.payload.circuit] = {
                state: 'success',
                data: action.payload.data
            };
        },
        setCircuitLocale: (state: CircuitSliceState, action: PayloadAction<number>): void => {
            state.circuit_locale =  action.payload;
        },
        setCurrencyList: (state: CircuitSliceState, action: PayloadAction<Currency[]>): void => {
            state.currency_list = action.payload;
        },
        toggleRefresh: (
            state
        ) => {
            state.refresh = !state.refresh;
        },
        updateCircuitInfoErrors: updateCircuitInfoErrorsReducer,
        addFlightParamsListConfigurationPreference: addFlightParamsListConfigurationPreferenceReducer,
        setFlightParamsListConfigurationPreference: setFlightParamsListConfigurationPreferenceReducer,
        setColumns: setColumnsReducer,
        toggleColumn: toggleColumnReducer,
        updateFlightParamsList: updateFlightParamsListReducer,
        updateFlightParamsListCriteria: updateFlightParamsListCriteriaReducer,
        updateFlightParams: updateFlightParamsReducer,
        updateFlightParamsListLoading: updateFlightParamsListLoadingReducer,
        updateFlightParamsListCount: updateFlightParamsListCountReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    toggleRefreshFlightParams,
    setFlightParams,
    setEditFlightParamsSetps,
    addPricesEditPriceRange,
    addPricesEditPeriod,
    addPricesEditTerrestrialPrice,
    addPricesEditFlightPrice,
    addPricesEditPromotion,
    addPricesTableEditAirport,
    addPricesTableEditAirportsList,
    addPricesTableEditPeriod,
    deletePricesEditAirport,
    deletePricesEditPriceRange,
    deletePricesEditPeriod,
    deletePricesEditTerrestrialPrice,
    deletePricesEditPromotion,
    deletePricesEditFlightPrice,
    deletePricesTableEditAirport,
    deletePricesTableEditAirportsList,
    deletePricesTableEditPeriod,
    setCancelationRules,
    setGlobalCancelationRules,
    setPricesEditValue,
    setPricesEditParameters,
    setPricesEditPriceRanges,
    setPricesEditPeriodValue,
    setPricesEditTerrestrialPriceValue,
    setPricesEditFlightPriceValue,
    setPricesEditPromotionValue,
    setPricesTableEditValue,
    setPricesTableEditParameters,
    setPricesTableEditPeriodValue,
    setPricesTableEditPriceValue,
    setItinerary,
    setItineraryByDay,
    setProducts,
    updateItineraryStepPicture,
    updatePricesEditPeriods,
    updatePricesEditTerrestrialPrices,
    updatePricesEditFlightPrices,
    updatePricesEditPromotions,
    updateProducts,
    updateProductsStack,
    updateItineraryByDay,
    setCircuitLocale,
    setCurrencyList,
    toggleRefresh,
    updateCircuitInfoErrors,
    addFlightParamsListConfigurationPreference,
    setFlightParamsListConfigurationPreference,
    setColumns,
    toggleColumn,
    updateFlightParamsList,
    updateFlightParamsListCriteria,
    updateFlightParams,
    updateFlightParamsListLoading,
    updateFlightParamsListCount
} = circuitSlice.actions;